type CampaignContext = {
  campaign_id: string;
  campaign: string;
  source: string;
  medium: string;
  ad_id: string;
};

type DataLayerEvent = Record<string, unknown> & { event: string };

export const STORAGE_KEY_SESSION_ID = 'zopa_session_id';
export const STORAGE_KEY_ACQUISITION_ID = 'zopa_acquisition_id';
export const STORAGE_KEY_FIRST_TOUCH = 'zopa_first_touch';

// Mirrors the GA4 default session timeout so one zopa_session_id maps to one GA session.
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

type StoredSession = { lastActivity: number; id: string };

export const generateId = (prefix: string) => `${prefix}_${crypto.randomUUID()}`;

const readSession = (): StoredSession | undefined => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SESSION_ID);

    return raw ? (JSON.parse(raw) as StoredSession) : undefined;
  } catch {
    return undefined;
  }
};

const writeSession = (session: StoredSession) => {
  localStorage.setItem(STORAGE_KEY_SESSION_ID, JSON.stringify(session));
};

export const startOrResumeSession = (): { isNew: boolean; id: string } => {
  const now = Date.now();
  const existing = readSession();

  if (existing?.id && now - existing.lastActivity < SESSION_TIMEOUT_MS) {
    writeSession({ lastActivity: now, id: existing.id });

    return { id: existing.id, isNew: false };
  }

  const id = generateId('sess');
  writeSession({ lastActivity: now, id });

  return { isNew: true, id };
};

export const getAnalyticsIds = () => {
  if (typeof window === 'undefined') {
    return { zopa_session_id: '', acquisition_id: '' };
  }

  return {
    acquisition_id: localStorage.getItem(STORAGE_KEY_ACQUISITION_ID) ?? '',
    zopa_session_id: readSession()?.id ?? '',
  };
};

// The GA client id is the part of the _ga cookie after "GA1.1." and only exists once analytics consent is given.
export const getGaClientId = (): string => {
  if (typeof document === 'undefined') {
    return '';
  }

  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.([^;]+)/);

  return match ? match[1] : '';
};

export const getCampaignContext = (params: URLSearchParams): CampaignContext => ({
  campaign_id: params.get('campaign_id') ?? params.get('utm_id') ?? '',
  ad_id: params.get('ad_id') ?? params.get('utm_content') ?? '',
  campaign: params.get('utm_campaign') ?? '',
  source: params.get('utm_source') ?? '',
  medium: params.get('utm_medium') ?? '',
});

export const pushToDataLayer = (event: DataLayerEvent): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const session = readSession();
  if (session) {
    writeSession({ ...session, lastActivity: Date.now() });
  }

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(event);
};
